/**
 * AI 分析「设置模板」弹窗的 UI 常量。
 * 类型枚举与 prompts/save 的 template_category 字段一一对应，修改时需同步 api 类型定义。
 */
import type { TAiPromptTemplateCategory } from '@/pages/datalab/services/ai/api';

import type { IAiPromptOption } from '../types';
import { CUSTOM_APPROACH_VALUE } from './editorDraft';

/** 模板名称最大长度（与产品约定一致，对应接口 name_cn） */
export const AI_TEMPLATE_NAME_MAX_LENGTH = 500;

/**
 * 系统预置「默认洞察思路」展示名（中英文），禁止保存为同名用户模板。
 * 与 prompts/list 预置项 name 对齐；保存时需拦截以免与默认思路混淆。
 */
export const AI_TEMPLATE_RESERVED_DISPLAY_NAMES = [
  'Default Insight Approach',
  '默认洞察思路',
] as const;

/** 模板名称是否为系统保留名（英文不区分大小写，中文精确匹配） */
export const isReservedAiTemplateDisplayName = (rawName: string): boolean => {
  const trimmed = rawName.trim();
  if (!trimmed) {
    return false;
  }
  return AI_TEMPLATE_RESERVED_DISPLAY_NAMES.some((reserved) => {
    if (reserved === '默认洞察思路') {
      return trimmed === reserved;
    }
    return trimmed.toLowerCase() === reserved.toLowerCase();
  });
};

/**
 * 下拉 __custom__ 项「自定义洞察思路」展示名（中英文），禁止保存为同名用户模板。
 * 与 useAiPromptOptions 中 dbl_ai_analysis_settings_custom_approach 文案对齐。
 */
export const AI_TEMPLATE_CUSTOM_APPROACH_RESERVED_DISPLAY_NAMES = [
  'Custom Insight Approach',
  '自定义洞察思路',
] as const;

/** 已有分析思路/模板名称，供保存弹窗同名校验 */
export interface IExistingAiTemplateNameRef {
  name: string;
  category: TAiPromptTemplateCategory;
  /** prompts/list 的 user_id，personal 重名比对时须与当前登录用户一致 */
  userId?: string;
}

/** 模板创建人 user_id 归一化，与 list 映射、home.userInfo.userid 对齐 */
export const normalizeTemplateOwnerUserId = (
  id?: string | number,
): string => {
  if (id === undefined || id === null || id === '') {
    return '';
  }
  return String(id);
};

/**
 * 在分析思路下拉列表中按模板名称 + 类型定位项（与保存弹窗 name_cn / template_category 对齐）。
 */
export const findPromptOptionByTemplateName = (
  templateName: string,
  templateCategory: TAiPromptTemplateCategory,
  promptOptions: IAiPromptOption[],
): IAiPromptOption | undefined => {
  const trimmedName = templateName.trim();
  if (!trimmedName) {
    return undefined;
  }
  const normalizedCategory = normalizeTemplateCategory(templateCategory);
  return promptOptions.find((option) => {
    if (String(option.value) === CUSTOM_APPROACH_VALUE) {
      return false;
    }
    const optionName = String(
      option.info?.templateName ?? option.text ?? '',
    ).trim();
    const optionCategory = normalizeTemplateCategory(
      option.info?.templateCategory,
    );
    return optionName === trimmedName && optionCategory === normalizedCategory;
  });
};

/**
 * 指定模板类型下是否已有同名分析思路（trim 后精确匹配）。
 * @returns true：列表中已存在该名称，保存将覆盖后端记录，仅作提示不拦截提交。
 * personal：仅与 userId 等于 currentUserId 的项比对；game/public 仍按类型+名称全量比对。
 */
export const isDuplicateAiTemplateNameInCategory = (
  rawName: string,
  category: TAiPromptTemplateCategory,
  existingTemplates: IExistingAiTemplateNameRef[],
  currentUserId?: string,
): boolean => {
  const trimmed = rawName.trim();
  if (!trimmed || !existingTemplates.length) {
    return false;
  }
  const normalizedCurrentUserId = normalizeTemplateOwnerUserId(currentUserId);
  return existingTemplates.some((item) => {
    if (item.category !== category) {
      return false;
    }
    // personal：他人同名模板不参与重名提示；无当前用户 id 时不比对 personal
    if (category === 'personal') {
      if (!normalizedCurrentUserId) {
        return false;
      }
      if (
        normalizeTemplateOwnerUserId(item.userId) !== normalizedCurrentUserId
      ) {
        return false;
      }
    }
    const existingName = item.name.trim();
    return Boolean(existingName) && existingName === trimmed;
  });
};

/** 模板名称是否为「自定义洞察思路」系统保留名（英文不区分大小写，中文精确匹配） */
export const isReservedCustomApproachDisplayName = (rawName: string): boolean => {
  const trimmed = rawName.trim();
  if (!trimmed) {
    return false;
  }
  return AI_TEMPLATE_CUSTOM_APPROACH_RESERVED_DISPLAY_NAMES.some((reserved) => {
    if (reserved === '自定义洞察思路') {
      return trimmed === reserved;
    }
    return trimmed.toLowerCase() === reserved.toLowerCase();
  });
};

/** 设置模板弹窗 formatMessage 最小类型 */
export type TAiTemplateNameFormatMessage = (
  descriptor: { id: string; },
  values?: Record<string, string | number>,
) => string;

export interface ITemplateNameValidationResult {
  /** 非空表示阻断保存 */
  error: string;
  /** 非空表示同名覆盖提示，不阻断保存 */
  warning: string;
}

/**
 * 模板名称完整校验（必填 / 保留名 / 长度 / 同类型重名）。
 * 输入、切换类型、打开弹窗时统一调用，保证实时反馈口径一致。
 */
export const resolveTemplateNameValidation = (
  rawName: string,
  category: TAiPromptTemplateCategory,
  formatMessage: TAiTemplateNameFormatMessage,
  existingTemplates: IExistingAiTemplateNameRef[],
  currentUserId?: string,
): ITemplateNameValidationResult => {
  if (!rawName.trim()) {
    return {
      error: formatMessage({ id: 'dbl_ai_analysis_template_name_required' }),
      warning: '',
    };
  }
  if (isReservedAiTemplateDisplayName(rawName)) {
    return {
      error: formatMessage({ id: 'dbl_ai_analysis_template_name_reserved' }),
      warning: '',
    };
  }
  if (isReservedCustomApproachDisplayName(rawName)) {
    return {
      error: formatMessage({
        id: 'dbl_ai_analysis_template_name_reserved_custom',
      }),
      warning: '',
    };
  }
  if (rawName.length > AI_TEMPLATE_NAME_MAX_LENGTH) {
    return {
      error: formatMessage(
        { id: 'dbl_ai_analysis_template_name_max_length' },
        { count: AI_TEMPLATE_NAME_MAX_LENGTH },
      ),
      warning: '',
    };
  }
  const isDuplicate = isDuplicateAiTemplateNameInCategory(
    rawName,
    category,
    existingTemplates,
    currentUserId,
  );
  if (isDuplicate) {
    return {
      error: '',
      warning: formatMessage({ id: 'dbl_ai_analysis_template_name_duplicate' }),
    };
  }
  return { error: '', warning: '' };
};

/** 可选模板类型顺序：个人 → 业务 → 公共 */
export const AI_TEMPLATE_CATEGORY_OPTIONS: TAiPromptTemplateCategory[] = [
  'personal',
  'game',
  'public',
];

/** 模板类型保存权限上下文，字段来自 privilegesConfig model */
export interface IAiTemplatePermissionContext {
  /** sys_admin：系统管理员 */
  isPermissionAdmin: boolean;
  /** game_manager：游戏/业务管理员 */
  isPermissionManager: boolean;
}

/**
 * 判断当前账号是否可保存指定类型的 AI 分析模板。
 * - personal：均可设置
 * - game：game_manager 或 sys_admin
 * - public：仅 sys_admin
 */
export const canSaveAiTemplateCategory = (
  category: TAiPromptTemplateCategory,
  permissions: IAiTemplatePermissionContext,
): boolean => {
  switch (category) {
    case 'personal':
      return true;
    case 'game':
      return permissions.isPermissionManager || permissions.isPermissionAdmin;
    case 'public':
      return permissions.isPermissionAdmin;
    default:
      return false;
  }
};

/** 无权限时回退到当前账号首个可选类型（personal 始终存在） */
export const getAllowedAiTemplateCategories = (
  permissions: IAiTemplatePermissionContext,
): TAiPromptTemplateCategory[] =>
  AI_TEMPLATE_CATEGORY_OPTIONS.filter((category) =>
    canSaveAiTemplateCategory(category, permissions),
  );

/** 无权限时回退到首个可选类型 */
export const resolveAllowedTemplateCategory = (
  preferred: TAiPromptTemplateCategory,
  permissions: IAiTemplatePermissionContext,
): TAiPromptTemplateCategory => {
  if (canSaveAiTemplateCategory(preferred, permissions)) {
    return preferred;
  }
  return getAllowedAiTemplateCategories(permissions)[0] ?? 'personal';
};

export const AI_TEMPLATE_HINT_I18N_KEY: Record<TAiPromptTemplateCategory, string> = {
  personal: 'dbl_ai_analysis_template_hint_personal',
  game: 'dbl_ai_analysis_template_hint_game',
  public: 'dbl_ai_analysis_template_hint_public',
};

export const AI_TEMPLATE_LABEL_I18N_KEY: Record<TAiPromptTemplateCategory, string> = {
  personal: 'dbl_ai_analysis_template_type_personal',
  game: 'dbl_ai_analysis_template_type_game',
  public: 'dbl_ai_analysis_template_type_public',
};

/** 将接口 template_category 规范为可选枚举，非法或缺失时回退 personal */
export const normalizeTemplateCategory = (
  value?: string | null,
): TAiPromptTemplateCategory => {
  if (
    value &&
    AI_TEMPLATE_CATEGORY_OPTIONS.includes(value as TAiPromptTemplateCategory)
  ) {
    return value as TAiPromptTemplateCategory;
  }
  return 'personal';
};
